import type { CircuitName, ProvingManifest } from "./proofs";
import {
  PROVING_MANIFEST_TIMEOUT_MS,
  readProvingResource,
} from "./readProvingResource";

export type V2DeploymentConfig = {
  chainId: string;
  proxyContract: string;
  lcdUrl: string;
  backendUrl?: string;
  /** Read-only receipt fallback when the LCD transaction index is unavailable. */
  receiptRpcUrl?: string;
  provingManifestUrl: string;
  expectedProxyCodeId: number;
  expectedSubwalletCodeId: number;
  expectedSubwalletCodeChecksum: string;
  expectedProxyAdmin?: string;
  allowDevelopmentArtifacts: boolean;
  /** Present only for an explicitly opted-in, origin-pinned testing build. */
  testingOrigin?: string;
};

export type SignedProvingManifest = {
  version: 2;
  environment: "production" | "development";
  ceremonyTranscriptSha256: string;
  circuits: ProvingManifest;
};

const circuits: CircuitName[] = [
  "reserve_deposit",
  "reserve_spend",
  "reserve_merge",
  "control_register",
  "control_action",
  "create_subwallet",
];

const required = (name: string): string => {
  const value = import.meta.env[name]?.trim();
  if (!value) throw new Error(`Private Mode requires ${name}`);
  return value;
};

const requiredCodeId = (name: string): number => {
  const value = required(name);
  if (!/^\d+$/.test(value))
    throw new Error(`Private Mode requires a valid ${name}`);
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 1) {
    throw new Error(`Private Mode requires a valid ${name}`);
  }
  return parsed;
};

const requiredSha256 = (name: string): string => {
  const value = required(name);
  if (!/^[0-9a-f]{64}$/.test(value)) {
    throw new Error(`Private Mode requires a lowercase SHA-256 ${name}`);
  }
  return value;
};

export const isV2Enabled = (): boolean =>
  import.meta.env.VITE_REDACTED_V2_ENABLED === "true";

const assertTestingOrigin = (expected: string): void => {
  let parsed: URL;
  try {
    parsed = new URL(expected);
  } catch {
    throw new Error("Private Mode testing requires a canonical HTTPS origin");
  }
  if (
    parsed.protocol !== "https:" ||
    parsed.username ||
    parsed.password ||
    expected !== parsed.origin
  ) {
    throw new Error("Private Mode testing requires a canonical HTTPS origin");
  }
  if (globalThis.location?.origin !== expected) {
    throw new Error(
      "This Private Mode testing build is not approved for this origin"
    );
  }
};

const artifactPolicyForBuild = (): Pick<
  V2DeploymentConfig,
  "allowDevelopmentArtifacts" | "testingOrigin"
> => {
  const profile =
    import.meta.env.VITE_REDACTED_V2_DEPLOYMENT_PROFILE?.trim() || "production";
  if (profile !== "production" && profile !== "testing") {
    throw new Error(
      "Private Mode deployment profile must be production or testing"
    );
  }
  const optedIn =
    import.meta.env.VITE_REDACTED_V2_ALLOW_DEV_ARTIFACTS === "true";
  if (profile === "testing") {
    if (!optedIn) {
      throw new Error(
        "Private Mode testing requires explicit development-artifact opt-in"
      );
    }
    const testingOrigin = required("VITE_REDACTED_V2_TEST_ORIGIN");
    assertTestingOrigin(testingOrigin);
    return { allowDevelopmentArtifacts: true, testingOrigin };
  }
  // The allow flag alone never enables rehearsal keys in an ordinary release.
  // Existing local development remains supported without a remote test profile.
  return { allowDevelopmentArtifacts: import.meta.env.DEV && optedIn };
};

export const deploymentConfig = (): V2DeploymentConfig => {
  if (!isV2Enabled()) throw new Error("Private Mode is not enabled");
  const expectedProxyAdmin = required("VITE_REDACTED_V2_EXPECTED_PROXY_ADMIN");
  const artifactPolicy = artifactPolicyForBuild();
  return {
    chainId: required("VITE_REDACTED_V2_CHAIN_ID"),
    receiptRpcUrl:
      import.meta.env.VITE_REDACTED_V2_RECEIPT_RPC_URL?.trim()?.replace(
        /\/$/,
        ""
      ),
    proxyContract: required("VITE_REDACTED_V2_PROXY_CONTRACT"),
    lcdUrl: required("VITE_REDACTED_V2_LCD_URL").replace(/\/$/, ""),
    backendUrl: import.meta.env.VITE_REDACTED_V2_BACKEND_URL?.trim()?.replace(
      /\/$/,
      ""
    ),
    provingManifestUrl: required("VITE_REDACTED_V2_PROVING_MANIFEST_URL"),
    expectedProxyCodeId: requiredCodeId(
      "VITE_REDACTED_V2_EXPECTED_PROXY_CODE_ID"
    ),
    expectedSubwalletCodeId: requiredCodeId(
      "VITE_REDACTED_V2_EXPECTED_SUBWALLET_CODE_ID"
    ),
    expectedSubwalletCodeChecksum: requiredSha256(
      "VITE_REDACTED_V2_EXPECTED_SUBWALLET_CODE_CHECKSUM"
    ),
    expectedProxyAdmin:
      expectedProxyAdmin.toLowerCase() === "none"
        ? undefined
        : expectedProxyAdmin,
    ...artifactPolicy,
  };
};

const manifestCache = new Map<string, Promise<ProvingManifest>>();

export const loadProvingManifest = (
  config: V2DeploymentConfig,
  fetchImpl: typeof fetch = fetch
): Promise<ProvingManifest> => {
  // Recheck before using cached artifacts as well as before the initial fetch.
  // An explicitly testing build must never silently become origin-independent.
  if (config.testingOrigin) {
    try {
      assertTestingOrigin(config.testingOrigin);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  const cacheKey = JSON.stringify([
    config.chainId,
    config.proxyContract,
    config.provingManifestUrl,
    config.allowDevelopmentArtifacts,
    config.testingOrigin ?? null,
  ]);
  const cached = manifestCache.get(cacheKey);
  if (cached) return cached;
  const pending = (async () => {
    const value = await readProvingResource<Partial<SignedProvingManifest>>(
      fetchImpl,
      config.provingManifestUrl,
      (response) => response.json(),
      PROVING_MANIFEST_TIMEOUT_MS
    );
    if (
      value.version !== 2 ||
      (value.environment !== "production" &&
        value.environment !== "development") ||
      typeof value.ceremonyTranscriptSha256 !== "string" ||
      !/^[0-9a-f]{64}$/.test(value.ceremonyTranscriptSha256) ||
      !value.circuits ||
      typeof value.circuits !== "object"
    ) {
      throw new Error("Invalid Private Mode proving manifest");
    }
    if (
      value.environment !== "production" &&
      !config.allowDevelopmentArtifacts
    ) {
      throw new Error(
        "Development proving artifacts are forbidden by this build"
      );
    }
    for (const circuit of circuits) {
      const artifact = value.circuits[circuit];
      if (
        !artifact ||
        typeof artifact.wasmUrl !== "string" ||
        typeof artifact.provingKeyUrl !== "string" ||
        !/^[0-9a-f]{64}$/.test(artifact.wasmSha256) ||
        !/^[0-9a-f]{64}$/.test(artifact.provingKeySha256)
      ) {
        throw new Error(`Invalid Private Mode artifact entry for ${circuit}`);
      }
    }
    return value.circuits;
  })();
  manifestCache.set(cacheKey, pending);
  // A failed mobile download must not poison every later explicit attempt.
  void pending.catch(() => {
    if (manifestCache.get(cacheKey) === pending) manifestCache.delete(cacheKey);
  });
  return pending;
};
